// Author: Sunil Jain
// Timestamp: 2025-05-04T07:22:00AM EDT
// File: src/pages/LaunchPage.js
// Description: Handles the initial SMART on FHIR launch sequence.

import React, { useEffect, useState } from 'react';
import FHIR from 'fhirclient'; // Import the library
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import logger from '../utils/logger'; // Assuming logger exists

function LaunchPage() {
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState('Initializing SMART launch sequence...');

    useEffect(() => {
        logger.info("LaunchPage: Component mounted, initiating SMART authorization.");
        setStatusMessage('Attempting to authorize with EHR...');

        // Configuration for the SMART launch
        // These details often come from the EHR's registration for your app
        // or potentially environment variables.
        const clientId = process.env.REACT_APP_SMART_CLIENT_ID || 'YOUR_CLIENT_ID'; // Replace with your actual client ID or use env var
        const redirectUri = process.env.REACT_APP_SMART_REDIRECT_URI || '/'; // The URI EHR redirects back to *after* user auth. Often the app root.
        // Scope defines the permissions your app requests (e.g., patient data, user info)
        // Consult EHR documentation for available scopes.
        const scope = process.env.REACT_APP_SMART_SCOPE || 'launch launch/patient openid fhirUser patient/*.read'; // Example scopes

        // Basic check for required config
        if (clientId === 'YOUR_CLIENT_ID') {
             logger.error("LaunchPage: Missing SMART Client ID configuration.");
             setError("Application is not configured correctly for SMART launch (Missing Client ID).");
             setStatusMessage('Configuration Error.');
             return;
        }

        logger.debug(`LaunchPage: Authorizing with clientId: ${clientId}, redirectUri: ${redirectUri}, scope: ${scope}`);

        // This function handles the interaction with the EHR's authorization server.
        // It will typically redirect the browser to the EHR login/authorization page.
        // If successful, the EHR redirects back to your `redirectUri`.
        // If this launch page itself *is* the redirectUri, fhirclient handles the code exchange.
        // If the redirectUri is different (e.g., '/'), fhirclient handles the redirect there,
        // and the logic in the main App component will pick it up.
        FHIR.oauth2.authorize({
            clientId: clientId,
            redirectUri: redirectUri,
            scope: scope,
            // iss: Typically provided by EHR in launch parameters, fhirclient usually handles this automatically.
        }).catch(err => {
            logger.error("LaunchPage: SMART authorization failed.", err);
            setError(`Authorization failed: ${err.message || 'Unknown error'}`);
            setStatusMessage('Authorization Error.');
        });

        // This component typically doesn't render anything meaningful long-term,
        // as it should redirect away almost immediately. The messages are fallback/debugging.

    }, []); // Run only once on mount

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '80vh', // Take up most of the viewport height
                p: 3,
            }}
        >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
                {statusMessage}
            </Typography>
            {error && (
                <Alert severity="error" sx={{ mt: 2, width: '100%', maxWidth: '600px' }}>
                    {error}
                </Alert>
            )}
             <Typography variant="caption" sx={{ mt: 2, color: 'text.secondary' }}>
                You should be redirected to the EHR authorization page shortly.
            </Typography>
        </Box>
    );
}

export default LaunchPage;
